import React, { useCallback, useEffect, useState } from 'react';
import { kanal, komeJe, trazPaznju } from '../poruke/oznake';
import Dialog from './Dialog';
import VehiclePositions from './VehiclePositions';
import TourDetail from './TourDetail';
import PozadinaUzbuna from './PozadinaUzbuna';
import { useOsvezavanje } from '../osvezavanje';
import { api } from '../services/api';
import { useSignedIn } from '../session';
import {
  ExceptionDecisionOption,
  ChannelHealth,
  PozadinskiPosao,
  ClosedInErpOnTour,
  DeliveryOnRemovedStop,
  ErpWriteNeedingAttention,
  OnlineNeodlucena,
  GoodsInVehicle,
  OpenException,
  MessagingAccount,
  OutboxMessage,
  PostingToDo,
  ParkedGoods,
  ReservationLeg,
  TourDetail as Detail,
  TourProgress
} from '../types';


/** Osvežava se sam, jer operater ovaj ekran drži otvoren dok radi nešto drugo. */
const REFRESH_MS = 60_000;

/**
 * Šta se dešava na putu.
 *
 * Vozačev izuzetak bez ovog ekrana je zapis koji niko ne čita. Zato izuzeci
 * stoje na vrhu, pre napretka tura — napredak je informacija, izuzetak je posao
 * koji čeka.
 */
/** Ime kanala kako ga čovek zove, ne kako se zove u kodu. */
function kanalImenom(channel: string): string {
  switch (channel) {
    case 'Viber':
      return 'Viber';
    case 'Sms':
      return 'SMS';
    case 'Email':
      return 'mejl';
    default:
      return channel;
  }
}

/** Sat i minut iz vremena sa servera. */
function sat(kad: string): string {
  return new Date(kad).toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });
}

/** Minuti kao „6h25" — kratko, jer stoji u redu ture. */
function trajanje(minuta: number): string {
  const h = Math.floor(minuta / 60);
  const m = minuta % 60;

  return h > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${m} min`;
}

export default function FieldMonitor() {
  const signedIn = useSignedIn();
  const [tours, setTours] = useState<TourProgress[]>([]);
  const [exceptions, setExceptions] = useState<OpenException[]>([]);
  // Da li je spisak izuzetaka STVARNO učitan. Bez ovoga je neuspelo učitavanje
  // pisalo „Nema prijavljenih izuzetaka" — laž baš kad ih je moglo biti (T8).
  const [exceptionsLoaded, setExceptionsLoaded] = useState(false);
  const [decisions, setDecisions] = useState<ExceptionDecisionOption[]>([]);
  const [leftInVehicles, setLeftInVehicles] = useState<GoodsInVehicle[]>([]);

  /**
   * Odeljci koji NISU stigli sa servera.
   *
   * Svaki od njih se traži zasebnim pozivom i njegov pad se hvata, da jedan
   * nedostupan ERP ne obori ceo ekran — to je ispravno. Ali odeljak se sakriva
   * kad je prazan, pa je pad izgledao isto kao „nema posla": nalozi za knjiženje
   * bez ijednog reda, zaglavljena roba bez ijednog komada.
   *
   * To je najgori smer greške na ovom ekranu, jer operater po njemu odlučuje da
   * NEMA šta da radi. Zato se sada imenuje šta nije stiglo.
   */
  const [nijeStiglo, setNijeStiglo] = useState<string[]>([]);

  // Skinute stanice na kojima je vozač ipak bio. Aplikacija ovo ne zatvara sama
  // — odluka naručioca 22.09.2026 — pa spisak stoji dok čovek ne odluči.
  const [nesaglasne, setNesaglasne] = useState<DeliveryOnRemovedStop[]>([]);

  /**
   * Otpremnice zatvorene u ERP-u, a roba im je još na turi i nije predata.
   *
   * Zatvaranje adrese je nemo: stanica ostaje vozaču na spisku, roba u vozilu, a
   * nigde ne piše da posao više ne postoji. Izmereno 24.09.2026: četiri takve
   * stanice, najstarija od 17.09.
   */
  const [zatvorene, setZatvorene] = useState<ClosedInErpOnTour[]>([]);

  /**
   * Ide li red poruka, po kanalu.
   *
   * Izmereno 24.09.2026: Akton vraca 401 od 18.09. i SEST DANA niko nije znao —
   * 73 poruke propale, medju njima 36 najava kupcu i 10 linkova ture vozacu.
   * Greska je bila u dnevniku servera, a na ekranu je stajalo 106 odbijenih koje
   * niko nije sklonio. Zato ovo stoji na VRHU i samo kad nesto stoji.
   */
  const [poruke, setPoruke] = useState<ChannelHealth[]>([]);
  /** Pozadinski poslovi — vidi `PozadinaUzbuna` (K22). */
  const [pozadina, setPozadina] = useState<PozadinskiPosao[]>([]);
  const [vracam, setVracam] = useState<DeliveryOnRemovedStop | null>(null);
  const [vracamRazlog, setVracamRazlog] = useState('');
  /** Prva noga u 012: je li posle mejla neko napravio nalog u ERP-u. */
  const [legs, setLegs] = useState<ReservationLeg[]>([]);
  const [parked, setParked] = useState<ParkedGoods[]>([]);
  const [parkedPrag, setParkedPrag] = useState(3);
  const [postings, setPostings] = useState<PostingToDo[]>([]);
  /**
   * Upisi u ERP koji nisu prošli. Spisak je postojao od početka, a do
   * 09.10.2026. ga nijedan ekran nije zvao — i to je sakrilo grešku zbog
   * koje nijedan prenos iz magacina 002 nikad nije prošao, sedam dana.
   */
  const [upisi, setUpisi] = useState<ErpWriteNeedingAttention[]>([]);
  /**
   * Online otpremnice o kojima se ne zna ko vozi. Online nema poslovođu koji
   * bi potvrdio trgovčev upis, pa je operater tu jedini čovek.
   */
  const [onlajn, setOnlajn] = useState<OnlineNeodlucena[]>([]);
  const [busyStopId, setBusyStopId] = useState<number | null>(null);
  const [busyPosting, setBusyPosting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Šta je poslednji potez uradio. Bez toga se ne zna da li se uopšte desio. */
  const [done, setDone] = useState<string | null>(null);

  /**
   * Otvorena tura. Drži se ŠIFRA, a stanice se dovlače uz nju — tako otvorena
   * tura može sama da se osvežava, a spisak tura ostaje lak: stanice se traže
   * tek kad ih neko zaista gleda.
   */
  /**
   * Šta se trenutno pita. Sve tri odluke traže tekst od čoveka, pa idu kroz isti
   * dijalog umesto kroz prozorčić pregledača — u njemu se nije videlo ni o kojoj
   * stanici je reč, ni koje su odluke moguće.
   */
  const [decidingOn, setDecidingOn] = useState<OpenException | null>(null);
  const [decision, setDecision] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const [abandoning, setAbandoning] = useState<TourProgress | null>(null);
  const [abandonReason, setAbandonReason] = useState('');

  /** Tura koju operater zatvara umesto vozača, i zašto. */
  const [closing, setClosing] = useState<TourProgress | null>(null);
  const [closeReason, setCloseReason] = useState('');

  /** Roba iz vozila o kojoj se odlučuje, i moguće odluke. */
  const [leftover, setLeftover] = useState<GoodsInVehicle | null>(null);
  const [leftoverChoice, setLeftoverChoice] = useState<string | null>(null);
  const [leftoverNote, setLeftoverNote] = useState('');
  const [leftoverOptions, setLeftoverOptions] = useState<ExceptionDecisionOption[]>([]);

  /** Tura kojoj se razresava kvar, i sta je operater odlucio. */
  const [kvar, setKvar] = useState<TourProgress | null>(null);
  const [kvarOdluka, setKvarOdluka] = useState('');

  const [withdrawing, setWithdrawing] = useState<PostingToDo | null>(null);
  const [withdrawNote, setWithdrawNote] = useState('');

  /** Poruke kupcima i vozačima — da se pad slanja vidi na vreme. */
  const [messages, setMessages] = useState<OutboxMessage[]>([]);
  const [account, setAccount] = useState<MessagingAccount | null>(null);
  /** Koja se odbijenica upravo sklanja — da dupli klik ne pošalje dva puta. */
  const [acknowledging, setAcknowledging] = useState<number | null>(null);

  const [openTourId, setOpenTourId] = useState<number | null>(null);
  const [openTour, setOpenTour] = useState<Detail | null>(null);
  /** Raste kad se tura izmeni, da se detalj povuče odmah a ne tek na sledeći krug. */
  const [tourRevision, setTourRevision] = useState(0);

  const load = useCallback(async () => {
    try {
      const [progress, open, left] = await Promise.all([
        api.tourProgress(),
        api.openExceptions(),
        api.goodsInVehicles()
      ]);
      setTours(progress);
      setExceptions(open);
      setExceptionsLoaded(true);
      setLeftInVehicles(left);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }

    // Nalozi se traže odvojeno: za njih treba ERP, pa njegov pad ne sme da
    // obori ceo ekran — izuzetak sa terena je važniji od knjiženja.
    // Skuplja se u jedan spisak i upisuje odjednom, na kraju — pet odvojenih
    // upisa stanja bi ekran preslagalo pet puta.
    const pali: string[] = [];

    try {
      setPostings(await api.postings());
    } catch {
      setPostings([]);
      pali.push('nalozi za knjiženje');
    }

    try {
      setUpisi(await api.writesNeedingAttention());
    } catch {
      setUpisi([]);
      pali.push('upisi u ERP koji nisu prošli');
    }

    try {
      setOnlajn(await api.onlineNeodlucene());
    } catch {
      setOnlajn([]);
      pali.push('online otpremnice bez odluke');
    }

    // Prenosi u 012 traže ERP; njegov pad ne sme da obori ekran.
    try {
      setLegs(await api.reservationLegs());
    } catch {
      setLegs([]);
      pali.push('prenosi u 012');
    }

    try {
      const zaglavljeno = await api.parkedGoods();
      setParked(zaglavljeno.stavke);
      setParkedPrag(zaglavljeno.pragDana);
    } catch {
      setParked([]);
      pali.push('roba zaglavljena u 012');
    }

    try {
      setNesaglasne(await api.deliveriesOnRemovedStops());
    } catch {
      setNesaglasne([]);
      pali.push('vozač je bio na skinutoj stanici');
    }

    try {
      setZatvorene(await api.closedInErpOnTours());
    } catch {
      setZatvorene([]);
      pali.push('zatvoreno u ERP-u a stoji na turi');
    }

    try {
      setPoruke(await api.outboxHealth());
    } catch {
      setPoruke([]);
      pali.push('stanje reda poruka');
    }

    try {
      setPozadina(await api.backgroundJobs());
    } catch {
      setPozadina([]);
      pali.push('stanje pozadinskih poslova');
    }

    try {
      setMessages(await api.messages());
    } catch {
      setMessages([]);
      pali.push('poruke');
    }

    try {
      setAccount(await api.messagingAccount());
    } catch {
      setAccount(null);
    }

    setNijeStiglo(pali);
  }, []);

  /**
   * Vraća skinutu stanicu na turu, jer je vozač na njoj ipak bio.
   *
   * Razlog je obavezan: ovo je ispravka tuđeg zapisa, i posle nje isporuka
   * ulazi u poslovođin izveštaj i u označavanje u ERP-u.
   */
  const vrati = useCallback(async () => {
    if (!vracam || vracamRazlog.trim().length === 0) {
      return;
    }

    setBusyStopId(vracam.stopId);
    try {
      await api.restoreStop(vracam.tourId, vracam.stopId, vracamRazlog.trim());
      setDone(`${vracam.stop} je vraćena na turu ${vracam.tourName}.`);
      setVracam(null);
      setVracamRazlog('');
      setNesaglasne(await api.deliveriesOnRemovedStops());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyStopId(null);
    }
  }, [vracam, vracamRazlog]);

  /**
   * „Pozvao sam ga." Sklanja odbijenicu sa spiska.
   *
   * Ne šalje poruku ponovo — kupcu sa fiksnim brojem SMS ne može stići ni iz
   * desetog pokušaja. Beleži da je neko video i postupio, i tek tada red
   * prestaje da traži pažnju.
   */
  const acknowledge = useCallback(async (id: number) => {
    setAcknowledging(id);
    try {
      await api.acknowledgeMessage(id);
      setMessages(await api.messages());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAcknowledging(null);
    }
  }, []);


  // Šifarnici razloga se traže JEDNOM: ne menjaju se u toku dana, a stajali su
  // u istom krugu sa osvežavanjem i išli na server svakog minuta.
  useEffect(() => {
    void api.exceptionDecisions().then(setDecisions).catch(() => setDecisions([]));
    void api.leftoverDecisions().then(setLeftoverOptions).catch(() => setLeftoverOptions([]));
  }, []);

  useOsvezavanje(load, REFRESH_MS);

  // Otvorena tura se osvežava kao i ostatak ekrana. Bez toga bi operater gledao
  // stanje od pre nekoliko minuta baš dok čeka da vozač javi da je stigao.
  useEffect(() => {
    if (openTourId === null) {
      setOpenTour(null);
      return;
    }

    let stopped = false;

    const fetchDetail = () => {
      api.tourDetail(openTourId)
        .then((detail) => {
          if (!stopped) setOpenTour(detail);
        })
        .catch((e) => {
          if (!stopped) setError(e instanceof Error ? e.message : String(e));
        });
    };

    fetchDetail();
    const timer = window.setInterval(fetchDetail, REFRESH_MS);

    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [openTourId, tourRevision]);

  /**
   * Odbacuje turu koja se neće voziti.
   *
   * Traži razlog jer odbacivanje OSLOBAĐA ROBU — ona se posle toga ponovo nudi
   * za planiranje. Tura ostaje u bazi sa svim što je zapisano.
   */
  const abandon = async () => {
    if (!abandoning || abandonReason.trim() === '') return;

    try {
      await api.abandonTour(abandoning.tourId, abandonReason.trim());

      setDone(`Tura „${abandoning.name}“ je odbačena — roba sa nje se ponovo nudi za planiranje.`);
      setAbandoning(null);
      setAbandonReason('');
      await load();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const decideOnLeftover = async () => {
    if (!leftover || leftoverChoice === null) return;

    try {
      await api.decideOnLeftover(leftover.tourId, leftoverChoice, leftoverNote.trim() || null);

      setDone(`Zabeleženo šta je urađeno sa robom iz vozila ${leftover.vehicle}.`);
      setLeftover(null);
      setLeftoverChoice(null);
      setLeftoverNote('');
      await load();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const resolve = async () => {
    if (!decidingOn || decision === null) return;

    setBusyStopId(decidingOn.stopId);
    try {
      await api.resolveException(
        decidingOn.stopId, decision, note.trim() || null, signedIn.displayName);

      setExceptions((current) => current.filter((e) => e.stopId !== decidingOn.stopId));
      setDecidingOn(null);
      setDecision(null);
      setNote('');
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyStopId(null);
    }
  };

  /**
   * Operater potvrđuje da naloga u ERP-u više nema.
   *
   * Aplikacija to ne zaključuje sama: prolazna greška u čitanju izgleda isto kao
   * brisanje, a posledica bi bila da roba ostane odvojena bez pokrića — ili
   * obrnuto, da se pusti u prodaju iako stoji u magacinu rezervacija.
   */
  /**
   * Operater sklanja javljanje o kvaru sa ekrana.
   *
   * Zapis o kvaru OSTAJE — iz njega se posle vidi koliko tura stane, kojim
   * vozilom i koliko to kosta. Sklanja se samo crvena traka.
   */
  const razresiKvar = async () => {
    if (!kvar || kvarOdluka.trim() === '') return;

    try {
      await api.resolveBreakdown(kvar.tourId, kvarOdluka.trim());
      setTours(await api.tourProgress());
      setKvar(null);
      setKvarOdluka('');
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  /**
   * „Urađeno rukom" za drugu nogu koja nije prošla.
   *
   * Endpoint je postojao, a nijedan ekran ga nije zvao — pa je savet „zatvori
   * kao urađeno rukom" upućivao na dugme koga nema. Analiza 2.0, 14.10.2026.
   */
  const [rukom, setRukom] = useState<ErpWriteNeedingAttention | null>(null);
  const [rukomNalog, setRukomNalog] = useState('');
  const [rukomBusy, setRukomBusy] = useState(false);

  const zatvoriRukom = async () => {
    if (!rukom?.document) return;

    setRukomBusy(true);
    try {
      await api.settleByHand(
        rukom.document, rukom.articleCode, rukomNalog.trim() || null,
        rukom.operation === 'povrat rezervacije' ? rukom.operation : undefined);

      setUpisi((current) => current.filter((u) => u.idempotencyKey !== rukom.idempotencyKey));
      setRukom(null);
      setRukomNalog('');
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRukomBusy(false);
    }
  };

  const withdraw = async () => {
    if (!withdrawing) return;

    setBusyPosting(withdrawing.documentNumber);
    try {
      const ishod = await api.withdrawWrite(
        withdrawing.idempotencyKey, signedIn.displayName, withdrawNote.trim() || null);

      setPostings((current) =>
        current.filter((p) => p.documentNumber !== withdrawing.documentNumber));

      setWithdrawing(null);
      setWithdrawNote('');
      setError(null);
      // Server kaže šta je poništenje značilo za lanac (otvorena rezervacija,
      // ponavljanje druge noge...) — to čovek mora da vidi, ne samo da red nestane.
      setDone(ishod.message);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyPosting(null);
    }
  };

  /**
   * Skače na odeljak. Isti potez kao na poslovođinom ekranu, samo bez
   * `useSections` — ovde odeljci nisu sklopivi, pa nema šta da se otvara.
   */
  const skoci = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /**
   * Šta na ovom ekranu traži operaterovu ruku, sa brojkom i putem do odeljka.
   *
   * `sad` je ono što ne čeka do sutra: odluka sa terena i tura koja stoji
   * otvorena od ranije — jedno drži kupca, drugo drži robu.
   */
  const cekaMene = [
    { id: 'ture-danas', broj: tours.filter((t) => t.isBrokenDown).length,
      tekst: ' vozilo je stalo', sad: true },
    { id: 'ture-danas', broj: tours.filter((t) => t.notOpenedYet).length,
      tekst: ' vozilo na putu, a tura nije otvorena', sad: true },
    { id: 'ceka-odluku', broj: exceptions.length,
      tekst: exceptions.length === 1 ? ' izuzetak čeka odluku' : ' izuzetaka čeka odluku',
      sad: true },
    { id: 'ture-danas', broj: tours.filter((t) => t.isStale).length,
      tekst: ' tura stoji otvorena od ranije', sad: true },
    { id: 'poruke',
      broj: messages.filter(trazPaznju).length,
      tekst: ' poruka nije prošla', sad: true },
    { id: 'stoji-u-012', broj: parked.filter((p) => p.isOverdue).length,
      tekst: ' stavki stoji u magacinu 012 predugo', sad: false },
    { id: 'prenos-u-012', broj: legs.filter((l) => !l.done).length,
      tekst: ' prenosa u 012 čeka', sad: false },
    { id: 'ostalo-u-vozilima', broj: leftInVehicles.length,
      tekst: ' tura sa robom koja je ostala u vozilu', sad: false },
    { id: 'zatvoreno-u-erpu', broj: zatvorene.length,
      tekst: ' u ERP-u zatvoreno a stoji na turi', sad: false },
    { id: 'skinute-stanice', broj: nesaglasne.length,
      tekst: ' predaja na skinutoj stanici', sad: false },
    { id: 'nalozi-za-knjizenje', broj: postings.length,
      tekst: ' nalog čeka knjiženje', sad: false },
    // GORI: pala rezervacija znači da roba NIJE odvojena, pa je drugi objekat
    // i dalje može prodati. Zato stoji sa `sad: true`.
    { id: 'neuspeli-upisi', broj: upisi.filter((u) => u.urgent).length,
      tekst: ' upis u ERP nije prošao — roba nije odvojena', sad: true },
    { id: 'neuspeli-upisi', broj: upisi.filter((u) => !u.urgent).length,
      tekst: ' upis u ERP traži pogled', sad: false },
    // Online bez odluke: hitno je samo ono gde piše da vozimo mi a adrese nema
    // — to je isporuka koja ne može u turu dok se mesto ne dopiše u ERP-u.
    { id: 'online-bez-odluke', broj: onlajn.filter((o) => o.urgent).length,
      tekst: ' online isporuka bez adrese, a vozimo je mi', sad: true },
    { id: 'online-bez-odluke', broj: onlajn.filter((o) => !o.urgent).length,
      tekst: ' online otpremnica bez odluke ko vozi', sad: false }
  ].filter((posao) => posao.broj > 0);

  return (
    <div className="monitor">
      {error && <div className="health health--bad">{error}</div>}
      {done && <div className="health health--ok">{done}</div>}

      {poruke.filter((k) => k.stuck).map((k) => (
        <div className="health health--bad" key={k.channel}>
          <strong>Poruke ne idu — {kanalImenom(k.channel)}.</strong>{' '}
          {k.pending} {k.pending === 1 ? 'poruka čeka' : 'poruka čeka'}, najstarija{' '}
          {k.oldestPendingMinutes} min.
          {k.lastSentAt && (
            <> Poslednja je prošla {new Date(k.lastSentAt).toLocaleString('sr-RS')}.</>
          )}
          {k.lastError && <> Servis kaže: <code>{k.lastError}</code>.</>}
          <br />
          Ovo nije zastoj u obradi — red se prazni svakog minuta. Kupac ne zna da mu
          roba stiže, a vozač možda nije dobio link ture. Proveri pristupne podatke
          servisa za poruke.
        </div>
      ))}

      <PozadinaUzbuna poslovi={pozadina} />

      {nijeStiglo.length > 0 && (
        <div className="health health--bad">
          <strong>Ovi delovi ekrana nisu stigli:</strong> {nijeStiglo.join(', ')}.
          <br />
          Prazno ovde <strong>ne znači</strong> da nema posla — znači da se nije
          moglo pročitati. Osveži za koji minut.
          <br />
          {/* RANIJE JE OVDE PISALO „najčešće je ERP baza nedostupna".
              07.10.2026. je to bilo netačno i odvelo traženje u pogrešnu
              stranu na pola sata: jedan od delova koji nisu stizali („stanje
              reda poruka") ERP ne dira uopšte, a pravi uzrok je bio naš —
              deljen račun je držao tuđu bazu i kvario veze iz poola. Zato
              ekran više ne pogađa uzrok nego kaže GDE on stoji. */}
          Ako se ponavlja i posle osvežavanja, uzrok nije ovde nego u dnevniku
          servera (<code>C:\elbraco-logistika\logs</code>, najnoviji{' '}
          <code>stdout_*.log</code>) — i ne mora biti ERP: ovako izgleda i kvar
          u našoj aplikaciji.
        </div>
      )}

      {/* ŠTA ČEKA TEBE, jednim pogledom.
          Ovaj ekran nosi devet odeljaka i dug je nekoliko ekrana skrolovanja, pa
          ono što ne pritiska na vrhu — ne pritiska nikako. Izmereno 24.09.2026:
          „Ostalo u vozilima" je sedmi odeljak i u njemu je stajalo 24 ture sa 165
          komada, najstarija od 22.08; a 28 kupčevih stanica na završenim turama
          nije imalo nijedan odgovor, najstarija od 18.08. Dugmad za sve to su
          postojala celo vreme.
          Isti obrazac je na poslovođinom ekranu i tamo radi: brojka VODI na svoj
          odeljak. */}
      {cekaMene.length > 0 && (
        <div className="todo">
          <ul className="todo__list">
            {cekaMene.map((posao) => (
              <li
                // Više poslova vodi u ISTI odeljak (isto sidro), pa samo sidro nije
                // jedinstven ključ — React je mešao redove (analiza 2.0, T10).
                key={`${posao.id}|${posao.tekst}`}
                className={posao.sad ? 'todo__item todo__item--now' : 'todo__item'}
              >
                <button
                  type="button"
                  className="todo__go"
                  onClick={() => skoci(posao.id)}
                >
                  <strong>{posao.broj}</strong>
                  {posao.tekst}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <section className={exceptions.length > 0 ? 'panel panel--alert' : 'panel'}>
        <h2 id="ceka-odluku">
          Čeka odluku {exceptions.length > 0 && <span className="count">{exceptions.length}</span>}
        </h2>

        {!exceptionsLoaded ? (
          <p className="empty">Izuzeci sa terena nisu učitani — ne zna se ima li ih. Osveži ekran.</p>
        ) : exceptions.length === 0 ? (
          <p className="empty">Nema prijavljenih izuzetaka sa terena.</p>
        ) : (
          <>
            <ul className="exceptions">
              {exceptions.map((item) => (
                <li key={item.stopId} className="exception">
                  <p className="exception__where">
                    {item.stopName}
                    {item.city ? `, ${item.city}` : ''}
                    <span className="muted">
                      {' '}· {item.tourName}
                      {item.driver ? ` · ${item.driver}` : ''}
                      {item.reportedAt ? ` · ${formatTime(item.reportedAt)}` : ''}
                    </span>
                  </p>

                  <p className="exception__reason">{item.reason}</p>

                  {/* Brojevi stoje uz izuzetak jer je prvi korak poziv kupcu.
                      Bez njih bi ih operater tražio po ERP-u i odlagao odluku. */}
                  {item.phones?.length > 0 && (
                    <p className="exception__phone">
                      {item.phones.map((number) => (
                        <a key={number} href={`tel:${number}`}>{number}</a>
                      ))}
                    </p>
                  )}

                  {item.undeliveredItems.length > 0 && (
                    <p className="exception__items">
                      Nije predato: {item.undeliveredItems.join(', ')}
                    </p>
                  )}

                  <div className="exception__actions">
                    <button
                      type="button"
                      className="primary"
                      disabled={busyStopId === item.stopId}
                      onClick={() => { setDecidingOn(item); setDecision(null); setNote(''); }}
                    >
                      Odluči
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {/* UPISI U ERP KOJI NISU PROŠLI.
          Stoji odmah ispod izuzetaka, a ne na dnu: pala rezervacija znači da
          roba NIJE odvojena u 012 i drugi objekat je i dalje može prodati.

          Ovaj spisak je postojao od početka — ruta `writes/attention`, polje
          `NeedsAttention`, servis koji ih vadi — a nijedan ekran ga nije zvao do
          09.10.2026. Cena te tišine je izmerena istog dana: dva od četiri zapisa
          bila su ista deterministička greška u dodeli broja naloga, zbog koje
          NIJEDAN prenos iz magacina 002 nikad nije prošao. Sedam dana.

          Zato uz red stoji SAVET, a poruka servera ispod njega: „Violation of
          PRIMARY KEY" čoveku ne kaže ni šta je ostalo nedovršeno ni šta sad da
          radi. Pravilo je u `NeuspeoUpis`, da se može ispitati bez ekrana. */}
      {upisi.length > 0 && (
        <section className={upisi.some((u) => u.urgent) ? 'panel panel--alert' : 'panel'}>
          <h2 id="neuspeli-upisi">
            Upis u ERP nije prošao <span className="count">{upisi.length}</span>
          </h2>

          <p className="note">
            Aplikacija je pokušala da upiše prenos i nije uspela, ili je prekinuta
            usred upisa. Red nestaje sam kad ponovni pokušaj prođe (drugu nogu
            aplikacija ponavlja na pet minuta), kad posao izgubi smisao, ili kad
            se za nedovršen upis u ERP-u nađe da je ipak prošao. Ostalo čeka tebe.
          </p>

          <ul className="rows">
            {upisi.map((upis) => (
              <li key={upis.idempotencyKey}>
                <span className="row__name">
                  {upis.operation}
                  {upis.urgent && <span className="flag flag--bad">roba nije odvojena</span>}
                </span>

                <span className="row__meta">
                  {upis.articleCode} × {upis.quantity} · {upis.from} → {upis.to}
                  {upis.document && ` · otpremnica ${upis.document}`}
                  {' '}· {upis.requestedBy} · {sat(upis.requestedAt)}
                  {' '}· stoji {trajanje(upis.waitingMinutes)}
                  {upis.state === 'Failed' && upis.lastAttemptAt && (
                    <> · poslednji pokušaj {sat(upis.lastAttemptAt)}</>
                  )}
                </span>

                <span className="row__note">{upis.advice}</span>

                {upis.failure && (
                  <span className="row__meta muted">
                    Servis kaže: <code>{upis.failure}</code>
                  </span>
                )}

                {upis.note && (
                  <span className="row__meta muted">{upis.note}</span>
                )}

                {/* Povrat je dugme dobio 16.10.2026: savet je slao na ručni
                    prenos, a zatvoriti ga nije imalo čime. */}
                {(upis.operation === 'zatvaranje rezervacije'
                  || upis.operation === 'povrat rezervacije') && upis.document && (
                  <span className="row__meta">
                    <button
                      type="button"
                      className="link"
                      onClick={() => { setRukom(upis); setRukomNalog(''); }}
                    >
                      Urađeno rukom
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ONLINE OTPREMNICE BEZ ODLUKE KO VOZI.
          Za online prodaju (090) pravilo je pozitivno: u logistiku ulazi samo
          ono što je izričito naše („elbs" u napomeni). Bez ovog spiska bi sve
          ostalo tiho ispadalo, a online prodaja bi čekala vozilo koje nikad
          nije naručeno.

          Operater je tu JEDINI čovek — online nema poslovođu koji bi potvrdio
          trgovčev upis. Zato uz svaki red stoji razlog: po njemu se zna šta
          pitati kad se pozove online, umesto „šta je ovo".

          Spisak je ograničen pragom (online_razvoz_od), pa počinje prazan i
          raste samo novim otpremnicama. Izmereno 09.10.2026: bez praga bi
          prvog dana imao 131 red istorije koja ne traži ništa. */}
      {onlajn.length > 0 && (
        <section className={onlajn.some((o) => o.urgent) ? 'panel panel--alert' : 'panel'}>
          <h2 id="online-bez-odluke">
            Online bez odluke <span className="count">{onlajn.length}</span>
          </h2>

          <p className="note">
            Online prodaja nije napisala ko vozi, ili je napisala nešto što se ne
            može pročitati. Dok to stoji ovako, isporuka <strong>ne ulazi u
            planiranje</strong> — pozovi online i zamoli da isprave napomenu u
            ERP-u.
          </p>

          <ul className="rows">
            {onlajn.map((red) => (
              <li key={red.documentKey}>
                <span className="row__name">
                  {red.documentKey}
                  {red.urgent && <span className="flag flag--bad">nema adrese</span>}
                </span>

                <span className="row__meta">
                  {red.customerName}
                  {red.city ? `, ${red.city}` : ' · mesto nije upisano'}
                  {' '}· {red.items} {red.items === 1 ? 'stavka' : 'stavki'}
                  {' '}· {red.waitingDays === 0
                    ? 'danas'
                    : `pre ${red.waitingDays} ${red.waitingDays === 1 ? 'dan' : 'dana'}`}
                </span>

                <span className="row__note">{red.razlog}</span>

                {/* Napomena kako je NAPISANA. Bez nje operater ne vidi šta je
                    trgovac pokušao, pa ne zna ni šta da mu kaže. */}
                <span className="row__meta muted">
                  Napomena: {red.notes ? <code>{red.notes}</code> : <em>prazna</em>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="panel">
        <h2 id="ture-danas">
          Ture danas
          {tours.some((t) => t.isStale) && (
            <span className="count"> · {tours.filter((t) => t.isStale).length} otvoreno od ranije</span>
          )}
          {tours.some((t) => t.isUpcoming) && (
            <span className="count"> · {tours.filter((t) => t.isUpcoming).length} za sutra</span>
          )}
        </h2>

        {tours.length === 0 ? (
          <p className="empty">Za danas nema nijedne ture.</p>
        ) : (
          <ul className="progress">
            {tours.map((tour) => (
              <li
                key={tour.tourId}
                className={`progress__row${openTourId === tour.tourId ? ' progress__row--open' : ''}`
                  + `${tour.isStale ? ' progress__row--stale' : ''}`}
              >
                {/* Ceo naziv je dugme: operater turu traži očima po imenu, pa bi
                    posebno dugme „otvori“ bilo još jedna meta da se promaši. */}
                <button
                  type="button"
                  className="progress__name progress__open"
                  onClick={() =>
                    setOpenTourId((current) => (current === tour.tourId ? null : tour.tourId))}
                >
                  {tour.name}
                  <span className="muted">
                    {' '}· {tour.driver ?? 'bez vozača'} · {tour.vehicle ?? 'bez vozila'}
                  </span>
                </button>

                <span className="progress__bar">
                  <span
                    className="progress__fill"
                    style={{
                      width: `${tour.totalStops > 0 ? (tour.finishedStops / tour.totalStops) * 100 : 0}%`
                    }}
                  />
                </span>

                <span className="progress__count">
                  {tour.finishedStops}/{tour.totalStops}
                </span>

                {/* KAD JE VOZILO IZASLO I KAD SE VRATILO.
                    Vozac dugmad pritiska na stanicama, pa `actualStart` i
                    `actualEnd` ne pokrivaju utovar, voznju do prve stanice ni
                    povratak. Zato stoje oba reda — i razlika medju njima. */}
                <span className="progress__vreme">
                  {tour.departedWarehouseAt && (
                    <span title={`izvor: ${tour.departedWarehouseSource ?? 'nepoznat'}`}>
                      vozilo {sat(tour.departedWarehouseAt)}
                      {tour.returnedToWarehouseAt && <>–{sat(tour.returnedToWarehouseAt)}</>}
                      {tour.vehicleDayMinutes !== null && <> ({trajanje(tour.vehicleDayMinutes)})</>}
                    </span>
                  )}
                  {tour.actualStart && (
                    <span className="muted">
                      {' '}· tura {sat(tour.actualStart)}
                      {tour.actualEnd && <>–{sat(tour.actualEnd)}</>}
                      {tour.recordedMinutes !== null && <> ({trajanje(tour.recordedMinutes)})</>}
                    </span>
                  )}
                </span>

                <span className="progress__flags">
                  {/* VOZILO STALO ide prvo. Sve ostalo na ovom ekranu ceka;
                      ovo stoji pored puta. */}
                  {tour.isBrokenDown && (
                    <>
                      <span className="flag flag--bad">
                        vozilo stalo
                        {tour.breakdownReportedAt && (
                          <> u {new Date(tour.breakdownReportedAt).toLocaleTimeString('sr-RS',
                            { hour: '2-digit', minute: '2-digit' })}</>
                        )}
                      </span>
                      <button
                        type="button"
                        className="link"
                        onClick={() => { setKvar(tour); setKvarOdluka(''); }}
                      >
                        rešeno
                      </button>
                    </>
                  )}
                  {tour.isStale && (
                    <span className="flag flag--warn">
                      od {new Date(tour.date).toLocaleDateString('sr-RS')}
                    </span>
                  )}
                  {/* Složena dan ranije. Siva, ne žuta — ništa ne fali, samo
                      njen dan još nije došao. */}
                  {tour.isUpcoming && (
                    <span className="flag">
                      za {new Date(tour.date).toLocaleDateString('sr-RS')}
                    </span>
                  )}
                  {tour.openExceptions > 0 && (
                    <span className="flag flag--bad">{tour.openExceptions} izuzetak</span>
                  )}
                  {/* Tiho ne znači pokvareno — vozač u zgradi nema signal. Ali
                      operater treba da zna da javljanja nema. */}
                  {tour.isQuiet && <span className="flag flag--warn">nema javljanja</span>}
                  {/* Vozilo je izaslo, a tura na telefonu nije otvorena — vozac
                      ne zna da ga aplikacija ne prati, a dan se meri kracim. */}
                  {tour.notOpenedYet && (
                    <span className="flag flag--warn">
                      vozilo na putu {tour.minutesSinceDeparture} min, tura nije otvorena
                    </span>
                  )}
                  {tour.actualEnd && <span className="flag flag--ok">završena</span>}
                  {/* Vozač ume da zaboravi „Završi turu", a dok tura stoji
                      otvorena roba na njoj je rezervisana i ne nudi se za
                      sutra. Zato operater sme da je zatvori. */}
                  {(tour.hasStarted ?? !!tour.actualStart) && !tour.actualEnd && (
                    <button
                      type="button"
                      className="link"
                      onClick={() => { setClosing(tour); setCloseReason(''); }}
                    >
                      zatvori
                    </button>
                  )}
                  {!(tour.hasStarted ?? !!tour.actualStart) && (
                    <>
                      <span className="flag">nije krenula</span>
                      {/* Samo dok tura stoji. Kad vozilo krene, roba je u njemu i
                          to se rešava izuzetkom, ne brisanjem odluke. */}
                      <button
                        type="button"
                        className="link link--danger"
                        onClick={() => { setAbandoning(tour); setAbandonReason(''); }}
                      >
                        odbaci
                      </button>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {openTourId !== null && (
        <section className="panel">
          {openTour
            ? (
              <TourDetail
                tour={openTour}
                onClose={() => setOpenTourId(null)}
                onChanged={() => {
                  setTourRevision((n) => n + 1);
                  void load();
                }}
              />
            )
            : <p className="muted">Učitavam turu…</p>}
        </section>
      )}

      {postings.length > 0 && (
        <section className="panel">
          <h2 id="nalozi-za-knjizenje">
            Nalozi za knjiženje <span className="muted">· {postings.length}</span>
          </h2>

          <p className="note">
            Prenosi koje je aplikacija upisala u ERP. Nalog postoji i roba je
            prebačena, ali <strong>nije proknjižen</strong> — knjiženje radi čovek u
            ERP-u, jer taj klik ulazi u glavnu i pomoćne knjige. Kad se proknjiži,
            nalog sam nestaje sa spiska.
            {/* MAGACINSKI PRENOSI (X) SE NE KNJIŽE i zato ih ovde nema.
                Pravilo naručioca od 09.10.2026. Izmereno istog dana: naših
                magacinskih naloga je 80 i svih 80 je neproknjiženo, dok je od
                122 maloprodajna 119 proknjiženo — dakle razlika je pravilo, a
                ne zaostatak. Dok su stajali ovde, spisak se nije mogao
                ispraznuti, a spisak koji se ne prazni prestaje da se čita. */}
            {' '}Prenosi <strong>magacin–magacin</strong> (nalog <code>X…</code>) se
            ne knjiže, pa se ovde i ne traže — pojave se samo ako ih u ERP-u nema.
          </p>

          <ul className="rows">
            {postings.map((posting) => (
              <li key={posting.documentNumber}>
                <span className="row__name">Nalog {posting.documentNumber}</span>

                <span className="row__meta">
                  {posting.operation} · {posting.from} → {posting.to}
                  {' '}· {posting.articleCode} × {posting.quantity}
                  {posting.documentKey && ` · otpremnica ${posting.documentKey}`}
                </span>

                <span className="row__side">
                {posting.missingInErp ? (
                  <>
                    <span className="flag flag--bad">nema ga u ERP-u</span>

                    {/* Aplikacija ovo ne zaključuje sama: prolazna greška u
                        čitanju izgleda isto kao brisanje. Zato pita čoveka. */}
                    <button
                      type="button"
                      disabled={busyPosting === posting.documentNumber}
                      onClick={() => { setWithdrawing(posting); setWithdrawNote(''); }}
                    >
                      Poništen je, oslobodi robu
                    </button>
                  </>
                ) : (
                  <span className={posting.waitingDays > 1 ? 'flag flag--warn' : 'flag'}>
                    {posting.waitingDays === 0
                      ? 'danas'
                      : `${posting.waitingDays} ${posting.waitingDays === 1 ? 'dan' : 'dana'}`}
                  </span>
                )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(messages.length > 0 || account !== null) && (
        <section className="panel">
          <h2 id="poruke">
            Poruke <span className="muted">· {messages.length}</span>
            {/* Broje se samo one koje niko nije sklonio: „pozvao sam ga" mora
                da ugasi crveno (T8). */}
            {messages.some(trazPaznju) && (
              <span className="flag flag--bad">
                {' '}{messages.filter(trazPaznju).length} nije prošlo
              </span>
            )}
          </h2>

          <p className="note">
            Kupcu ide SMS kad vozač krene, vozaču i poslovođi Viber čim se tura
            sačuva. „Čeka" duže od par minuta znači da slanje nije podešeno —
            proveri na kartici „Podešavanja".
          </p>

          <p className="note">
            Poruka koja nije prošla ostaje ovde i sutradan, dok je neko ne
            skloni. Najčešći razlog je da kupac u ERP-u ima samo fiksni broj —
            na njega SMS ne stiže nikad, pa ponovno slanje ne pomaže. Pomaže
            poziv. Kad ga obaviš, skloni poruku da spisak ostane kratak.
          </p>

          <ul className="storelist">
            {messages.map((message) => {
              const nijeProslo = message.status === 'Rejected' || message.status === 'Failed';
              const zaSklanjanje = nijeProslo && message.acknowledgedAt === null;

              return (
                <li key={message.id}>
                  <span className="storelist__where">
                    {komeJe(message.purpose)}
                    {' · '}
                    {message.destination ?? message.rawDestination ?? 'bez broja'}
                    <span className="muted">
                      {' '}· {kanal(message.channel)}
                      {message.tourId !== null && <> · tura {message.tourId}</>}
                      {/* Odbijenica preživljava dan, pa mora reći od kad je —
                          inače izgleda kao da se upravo desila. */}
                      {!jeDanasnja(message.createdAt) && (
                        <> · od {outboxDatum(message.createdAt)}</>
                      )}
                    </span>
                    {message.error && <span className="storelist__why muted">{message.error}</span>}
                    {message.acknowledgedBy && (
                      <span className="storelist__why muted">
                        sklonio {message.acknowledgedBy}
                      </span>
                    )}
                  </span>

                  {message.status === 'Sent' ? (
                    <span className="flag flag--ok">poslato</span>
                  ) : message.status === 'Pending' ? (
                    <span className="flag">čeka</span>
                  ) : (
                    <>
                      <span className={zaSklanjanje ? 'flag flag--bad' : 'flag'}>
                        {zaSklanjanje ? 'nije prošlo' : 'nije prošlo · rešeno'}
                      </span>
                      {zaSklanjanje && (
                        <button
                          type="button"
                          className="link"
                          disabled={acknowledging === message.id}
                          onClick={() => void acknowledge(message.id)}
                        >
                          {acknowledging === message.id ? 'sklanjam…' : 'pozvao sam ga'}
                        </button>
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <VehiclePositions />

      {decidingOn && (
        <Dialog
          title={`Odluka: ${decidingOn.stopName}`}
          description={
            <>
              <strong>{decidingOn.reason}</strong>
              {decidingOn.undeliveredItems.length > 0
                && <> · nije predato: {decidingOn.undeliveredItems.join(', ')}</>}
              <br />
              Stavka se ne zatvara sama — odluka govori šta se sa robom radi.
            </>
          }
          confirmLabel="Zabeleži odluku"
          busy={busyStopId === decidingOn.stopId}
          disabled={decision === null}
          onCancel={() => { setDecidingOn(null); setDecision(null); setNote(''); }}
          onConfirm={() => void resolve()}
        >
          <label>
            Šta se radi
            {decisions.length === 0 && (
              <span className="health health--bad">
                Spisak mogućih odluka nije stigao sa servera. Osveži stranicu.
              </span>
            )}
            <span className="choices">
              {decisions.map((d) => (
                <button
                  key={d.code}
                  type="button"
                  aria-pressed={decision === d.code}
                  onClick={() => setDecision(d.code)}
                >
                  {d.label}
                </button>
              ))}
            </span>
          </label>

          <label>
            Beleška (može ostati prazno)
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="npr. kupac traži isporuku u subotu pre podne"
            />
          </label>
        </Dialog>
      )}

      {closing && (
        <Dialog
          title={`Zatvoriti turu „${closing.name}"?`}
          description={
            <>
              Tura je krenula ali nije završena. Zatvaranje je oslobađa, a roba
              koja nije predata odmah ispada u <strong>Ostalo u vozilima</strong>,
              gde kažeš šta je s njom. Ovo ne proglašava ništa isporučenim.
            </>
          }
          confirmLabel="Zatvori turu"
          disabled={closeReason.trim() === ''}
          onConfirm={async () => {
            try {
              await api.closeTour(closing.tourId, closeReason.trim());
              setDone(`Tura „${closing.name}" je zatvorena.`);
              setClosing(null);
              setCloseReason('');
              await load();
              setError(null);
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            }
          }}
          onCancel={() => setClosing(null)}
        >
          <label className="field">
            <span>Zašto se zatvara</span>
            <input
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              placeholder="npr. vozač zaboravio da zatvori, roba istovarena"
            />
          </label>
        </Dialog>
      )}

      {kvar && (
        <Dialog
          title={`Vozilo na turi „${kvar.name}“ — šta je odlučeno?`}
          description={
            <>
              {kvar.breakdownNote
                ? <>Vozač je javio: <strong>{kvar.breakdownNote}</strong>. </>
                : <>Vozač je javio da je vozilo stalo. </>}
              U vozilu je roba sa {kvar.remainingStops}{' '}
              {kvar.remainingStops === 1 ? 'stanice' : 'stanica'} koje nisu obiđene.
              Zapis o kvaru ostaje zabeležen — sklanja se samo crvena traka.
            </>
          }
          confirmLabel="Zabeleži"
          disabled={kvarOdluka.trim() === ''}
          onCancel={() => { setKvar(null); setKvarOdluka(''); }}
          onConfirm={() => void razresiKvar()}
        >
          <label>
            Šta je odlučeno
            <input
              value={kvarOdluka}
              onChange={(e) => setKvarOdluka(e.target.value)}
              placeholder="npr. zamenjena guma, nastavlja; ili roba presla u SO193VE1"
            />
          </label>
        </Dialog>
      )}

      {abandoning && (
        <Dialog
          title={`Odbaciti turu „${abandoning.name}“?`}
          description={
            <>Roba sa nje se oslobađa i ponovo nudi za planiranje. Tura ostaje
            zapisana sa svim stanicama — ne briše se.
            {/* GPS je upozorenje, ne brana: računa se po vozilu i danu, pa ume
                da pripiše izlazak sa druge ture istog vozila (K4). */}
            {abandoning.departedWarehouseAt && (
              <><br /><br /><strong>Pažnja:</strong> po GPS-u je vozilo izašlo u{' '}
              {new Date(abandoning.departedWarehouseAt).toLocaleTimeString('sr-RS',
                { hour: '2-digit', minute: '2-digit' })}. Ako je to ova tura, vozač
              možda vozi bez signala — pozovi ga pre nego što odbaciš.</>
            )}</>
          }
          confirmLabel="Odbaci turu"
          danger
          disabled={abandonReason.trim() === ''}
          onCancel={() => { setAbandoning(null); setAbandonReason(''); }}
          onConfirm={() => void abandon()}
        >
          <label>
            Razlog
            <input
              value={abandonReason}
              onChange={(e) => setAbandonReason(e.target.value)}
              placeholder="npr. vozilo u kvaru, roba ide sutra"
            />
          </label>
        </Dialog>
      )}

      {rukom && (
        <Dialog
          title={rukom.operation === 'povrat rezervacije'
            ? 'Povrat 012 → izvor urađen rukom?'
            : 'Prenos 012 → objekat urađen rukom?'}
          description={
            <>
              Otpremnica <strong>{rukom.document}</strong>, artikal {rukom.articleCode}.
              <br /><br />
              Klikni samo ako je prenos <strong>stvarno napravljen</strong> u ERP-u.
              Aplikacija proverava da je komad izašao iz 012 — ako nije, ne prihvata.
              Posle ovoga aplikacija taj prenos više ne pokušava.
            </>
          }
          confirmLabel="Urađeno rukom"
          busy={rukomBusy}
          onCancel={() => { setRukom(null); setRukomNalog(''); }}
          onConfirm={() => void zatvoriRukom()}
        >
          <label>
            Broj naloga u ERP-u (može ostati prazno)
            <input
              value={rukomNalog}
              onChange={(e) => setRukomNalog(e.target.value)}
              placeholder="npr. 80008"
            />
          </label>
        </Dialog>
      )}

      {withdrawing && (
        <Dialog
          title={`Nalog ${withdrawing.documentNumber} je poništen u ERP-u?`}
          description={
            // Šta poništenje znači zavisi od noge. Do 15.10.2026. je za sve pisalo
            // „može ponovo da se rezerviše", a kod druge noge to nije tačno: roba
            // je i dalje u 012, i prenos se ponavlja (analiza 2.0, E5).
            withdrawing.operation === 'zatvaranje rezervacije' ? (
              <>Rezervacija se ponovo otvara: roba i dalje važi kao odvojena u 012, a
              drugu nogu (012 → objekat) aplikacija ponavlja sama u sledećem prolazu.
              Ako je prenos urađen rukom, aplikacija to vidi po stanju 012 i traži
              „Urađeno rukom" uz red. Zapis o upisu ostaje.</>
            ) : withdrawing.operation === 'povrat rezervacije' ? (
              <>Rezervacija se ponovo otvara: roba i dalje važi kao odvojena u 012, a
              povrat (012 → izvor) aplikacija ponavlja sama u sledećem prolazu. Zapis
              o upisu ostaje.</>
            ) : (
              <>Roba prestaje da važi kao odvojena i može ponovo da se rezerviše.
              Zapis o upisu ostaje, pa se kasnije vidi šta se desilo.</>
            )
          }
          confirmLabel="Oslobodi robu"
          danger
          busy={busyPosting === withdrawing.documentNumber}
          onCancel={() => { setWithdrawing(null); setWithdrawNote(''); }}
          onConfirm={() => void withdraw()}
        >
          <label>
            Beleška (može ostati prazno)
            <input
              value={withdrawNote}
              onChange={(e) => setWithdrawNote(e.target.value)}
              placeholder="npr. poslovođa javio da je greškom napravljen"
            />
          </label>
        </Dialog>
      )}

      {leftover && (
        <Dialog
          title={`Roba u vozilu ${leftover.vehicle}`}
          description={
            <>
              <strong>{leftover.quantity} kom</strong> sa ture {leftover.tourName} nije
              predato. Odluka se beleži i spisak se prazni — roba se za planiranje
              ionako već nudi, jer je tura završena.
            </>
          }
          confirmLabel="Zabeleži"
          disabled={leftoverChoice === null}
          onCancel={() => { setLeftover(null); setLeftoverChoice(null); setLeftoverNote(''); }}
          onConfirm={() => void decideOnLeftover()}
        >
          <label>
            Šta je urađeno
            {leftoverOptions.length === 0 ? (
              /* Prazan spisak nije prazan izbor nego kvar: server nije dao
                 moguće odluke. Ekran to kaže umesto da ostavi belinu ispod
                 naslova i dugme koje se ne da pritisnuti. */
              <span className="health health--bad">
                Spisak mogućih odluka nije stigao sa servera. Osveži stranicu; ako
                se ponovi, backend je stariji od ekrana.
              </span>
            ) : (
              <span className="choices">
                {leftoverOptions.map((option) => (
                  <button
                    key={option.code}
                    type="button"
                    aria-pressed={leftoverChoice === option.code}
                    onClick={() => setLeftoverChoice(option.code)}
                  >
                    {option.label}
                  </button>
                ))}
              </span>
            )}
          </label>

          <label>
            Beleška (može ostati prazno)
            <input
              value={leftoverNote}
              onChange={(e) => setLeftoverNote(e.target.value)}
              placeholder="npr. ostaje u kombiju za Suboticu"
            />
          </label>
        </Dialog>
      )}

      {parked.length > 0 && (
        <section
          className={parked.some((p) => p.isOverdue) ? 'panel panel--alert' : 'panel'}
        >
          <h2 id="stoji-u-012">
            Stoji u magacinu 012
            <span className="count">{parked.filter((p) => p.isOverdue).length}</span>
          </h2>

          <p className="note">
            Roba je izašla sa lagera objekta a nije stigla nikuda: iz 012 niko ne
            prodaje, a minus koji je prodajom nastao kod objekta stoji otvoren dok
            se druga noga ne odradi. Čita se saldo magacina iz ERP-a, pa se ovde
            vidi i ono što je preneto ručno, bez aplikacije. Preko {parkedPrag} dana
            je crveno — 012 je prolaz, ne skladište.
          </p>

          <ul className="rows">
            {parked.map((stavka) => (
              <li key={`${stavka.articleCode}-${stavka.nalog ?? '?'}`}>
                <span className="row__name">
                  {stavka.articleCode} × {stavka.quantity}
                </span>

                <span className="row__side">
                  {stavka.daysWaiting === null ? (
                    <span className="flag flag--warn">datum nepoznat</span>
                  ) : (
                    <span
                      className={stavka.isOverdue ? 'flag flag--bad' : 'flag flag--warn'}
                    >
                      stoji {stavka.daysWaiting} d
                    </span>
                  )}
                </span>

                <span className="row__meta">
                  dao {stavka.fromName} · odvojeno za {stavka.forName} · nalog{' '}
                  {stavka.nalog ?? '—'}
                  {stavka.isOverdue && (
                    <>
                      {' · '}
                      <strong>
                        proveri je li kupac robu već odneo sam — ako je, prenos
                        012 → {stavka.forName} nije napravljen
                      </strong>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {legs.length > 0 && (
        <section className={legs.some((l) => l.kasni) ? 'panel panel--alert' : 'panel'}>
          <h2 id="prenos-u-012">
            Prenos u magacin 012
            <span className="count">{legs.filter((l) => !l.done).length}</span>
          </h2>

          <p className="note">
            Poslovođa je odlučio odakle roba ide i objekat je dobio mejl. Ovde se
            vidi da li je nalog u ERP-u stvarno napravljen — dok nije, roba stoji
            na lageru objekta i može je prodati neko drugi. Nalog se priznaje samo
            ako ga je napravio izabrani objekat, i jedan nalog pokriva onoliko
            otpremnica koliko ima komada — nalog iz drugog objekta nije ova prva
            noga. Kad prenosa nema, a
            isti objekat je taj artikal u međuvremenu poslao u 002, i to piše —
            kroz 002 idu i sve redovne dopune, pa je to nagoveštaj za proveru, ne
            presuda.
          </p>

          <ul className="rows">
            {legs.map((leg) => (
              <li key={`${leg.documentKey}-${leg.articleCode}`}>
                <span className="row__name">
                  {leg.articleName} × {leg.quantity}
                </span>

                <span className="row__side">
                  {leg.done ? (
                    <span className="flag flag--ok">
                      nalog {leg.nalog ?? '—'}
                    </span>
                  ) : leg.pogresanMagacin ? (
                    <span className={leg.kasni ? 'flag flag--bad' : 'flag flag--warn'}>
                      možda u {leg.pogresanMagacin}
                    </span>
                  ) : (
                    <span className={leg.kasni ? 'flag flag--bad' : 'flag flag--warn'}>
                      čeka {Math.round(leg.hoursWaiting)} h
                    </span>
                  )}
                </span>

                <span className="row__meta">
                  {leg.sourceName} · otpremnica {leg.documentKey} · odlučio {leg.decidedBy}
                  {leg.pogresanMagacin && (
                    <>
                      {' · '}
                      <strong>
                        isti objekat je poslao ovaj artikal u {leg.pogresanMagacin}{' '}
                        (nalog {leg.pogresanNalog ?? '—'}) — proveri je li to
                        rezervacija promašila magacin ili obična dopuna
                      </strong>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {leftInVehicles.length > 0 && (
        <section className="panel">
          <h2 id="ostalo-u-vozilima">
            Ostalo u vozilima <span className="count">{leftInVehicles.length}</span>
          </h2>
          <p className="note">
            Roba sa završenih tura koja nije predata. Ujutru odlučuješ ostaje li u
            istom vozilu, vraća se u magacin, ili čeka drugo vozilo.
          </p>

          <ul className="rows">
            {leftInVehicles.map((entry) => (
              <li key={entry.tourId}>
                <span className="row__name">{entry.vehicle}</span>

                <span className="row__side">
                  <span className="flag flag--warn">{entry.quantity} kom</span>
                  <button
                    type="button"
                    onClick={() => {
                      setLeftover(entry);
                      setLeftoverChoice(null);
                      setLeftoverNote('');
                    }}
                  >
                    Odluči
                  </button>
                </span>

                <span className="row__meta">
                  {entry.tourName} · {formatDate(entry.date)} · {entry.items.join(', ')}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {zatvorene.length > 0 && (
        <section className="panel panel--todo">
          <h2 id="zatvoreno-u-erpu">
            U ERP-u zatvoreno, a stoji na turi{' '}
            <span className="count">{zatvorene.length}</span>
          </h2>
          <p className="note">
            Ove otpremnice su u ERP-u izašle iz spiska neisporučenih, a roba im je
            i dalje na turi i <strong>naš vozač nije predao ništa</strong>. Znači
            da je roba nekako otišla kupcu — objekat je isporučio sam, ili je kupac
            odneo.
            <br />
            Aplikacija ovo ne skida sama, jer druga mogućnost je da je neko u ERP-u
            kukičao prerano — a to bi ostavilo kupca bez robe. Proveri, pa skini
            stanicu sa ture ako posla više nema.
          </p>

          <ul className="rows">
            {zatvorene.map((red) => (
              <li key={red.stopId}>
                <span className="row__name">{red.stop}</span>

                <span className="row__side">
                  <span className="flag flag--bad">{red.quantity} kom</span>
                </span>

                <span className="row__meta">
                  {red.tourName} · {formatDate(red.date)} · {red.tourState}
                  {red.driver ? ` · ${red.driver}` : ''}
                  {red.city ? ` · ${red.city}` : ''}
                </span>

                <span className="row__meta muted">
                  {red.documentKey}
                  {red.arrivedAt ? ` · vozač stigao ${formatDate(red.arrivedAt)}` : ''}
                  {red.items.length > 0 ? ` · ${red.items.join(', ')}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {nesaglasne.length > 0 && (
        <section className="panel panel--todo">
          <h2 id="skinute-stanice">
            Vozač je bio na skinutoj stanici{' '}
            <span className="count">{nesaglasne.length}</span>
          </h2>
          <p className="note">
            Zapis govori dve stvari: tačka je skinuta sa ture, a roba je na njoj
            predata. Zbog toga ta isporuka <strong>ne stoji nigde</strong> — ni u
            poslovođinom izveštaju, ni na spisku za knjiženje u ERP-u.
            <br />
            Aplikacija ovo ne zatvara sama, jer druga mogućnost je da je vozač
            štriklirao greškom — a to se iz podataka ne vidi. Ako je roba stvarno
            predata, vrati tačku na turu i sve dalje ide samo.
          </p>

          <ul className="rows">
            {nesaglasne.map((red) => (
              <li key={red.stopId}>
                <span className="row__name">{red.stop}</span>

                <span className="row__side">
                  <span className="flag flag--bad">{red.quantity} kom</span>
                  <button
                    type="button"
                    disabled={busyStopId === red.stopId}
                    onClick={() => {
                      setVracam(red);
                      setVracamRazlog('');
                    }}
                  >
                    Vrati na turu
                  </button>
                </span>

                <span className="row__meta">
                  {red.tourName} · {formatDate(red.date)}
                  {red.driver ? ` · ${red.driver}` : ''}
                  {red.documents.length > 0 ? ` · ${red.documents.join(', ')}` : ''}
                </span>

                <span className="row__meta muted">
                  skinuta {formatDate(red.removedAt)}
                  {red.removedBy ? ` (${red.removedBy})` : ''}
                  {red.removedReason ? ` — „${red.removedReason}"` : ''}
                  {red.arrivedAt ? ` · vozač stigao ${formatDate(red.arrivedAt)}` : ''}
                  {red.hasSignature ? ' · ima potpis' : ' · bez potpisa'}
                  {red.items.length > 0 ? ` · ${red.items.join(', ')}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {vracam && (
        <Dialog
          title={`Vrati „${vracam.stop}" na turu`}
          description={
            <>
              Posle ovoga isporuka ulazi u poslovođin izveštaj i na spisak za
              knjiženje u ERP-u.
              <br />
              Razlog ostaje u zapisu izmene ture — ovo je ispravka tuđeg zapisa.
            </>
          }
          confirmLabel="Vrati na turu"
          busy={busyStopId === vracam.stopId}
          disabled={vracamRazlog.trim().length === 0}
          onCancel={() => { setVracam(null); setVracamRazlog(''); }}
          onConfirm={() => void vrati()}
        >
          <label>
            Odakle znaš da je roba predata
            <textarea
              value={vracamRazlog}
              onChange={(e) => setVracamRazlog(e.target.value)}
              placeholder="npr. vozač potvrdio telefonom, kupac se javio"
              rows={3}
            />
          </label>
        </Dialog>
      )}
    </div>
  );
}

function formatTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('sr-RS');
}

/**
 * Vreme nastanka poruke je jedino vreme na ovom ekranu koje se upisuje u UTC-u
 * — sva ostala (dolazak, polazak, potpis) idu lokalnim satom. A stiže bez „Z",
 * pa bi ga pregledač pročitao kao lokalno i pomerio dva sata unazad: poruka
 * nastala u 00:33 prikazala bi se kao jučerašnja.
 *
 * Zato se čita ovde, odvojeno, i ne dira se `formatDate` — on služi vremenima
 * koja jesu lokalna.
 */
function outboxDatum(value: string): string {
  const utc = /[Zz]$|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`;
  const date = new Date(utc);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('sr-RS');
}

/** Da li je poruka nastala danas — odbijenice od ranije moraju reći od kad su. */
function jeDanasnja(value: string): boolean {
  return outboxDatum(value) === new Date().toLocaleDateString('sr-RS');
}
